import React, { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Search, RefreshCcw, Plus, Cpu, Bot, Workflow } from "lucide-react";
import StatusPill from "@/components/aieco/StatusPill";
import { discovery } from "@/data/mockData";

const TYPE_ICON = {
  "LLM Provider": Cpu,
  "Business Agent": Workflow,
  "AI Assistant": Bot,
};

const STATUS_TONE = { Healthy: "success", Review: "warning", Critical: "critical" };
const RISK_TONE = { Low: "success", Medium: "warning", High: "critical" };

export default function AiDiscovery() {
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("All");
  const [items, setItems] = useState(discovery);

  const filtered = useMemo(() => {
    return items.filter((it) => {
      const matchesQ = !q || `${it.name} ${it.type} ${it.endpoint}`.toLowerCase().includes(q.toLowerCase());
      const matchesF = filter === "All" || it.type === filter;
      return matchesQ && matchesF;
    });
  }, [items, q, filter]);

  const register = (id) =>
    setItems((prev) => prev.map((p) => (p.id === id ? { ...p, registered: true } : p)));

  const stats = useMemo(() => {
    const total = items.length;
    const unregistered = items.filter((i) => !i.registered).length;
    const critical = items.filter((i) => i.status === "Critical").length;
    return { total, unregistered, critical };
  }, [items]);

  return (
    <div className="mx-auto max-w-[1480px] px-6 py-8 lg:px-10 lg:py-10" data-testid="page-discovery">
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex flex-wrap items-end justify-between gap-4"
      >
        <div>
          <div className="text-[11px] uppercase tracking-[0.18em] text-slate-400">AI Discovery</div>
          <h1 className="mt-2 font-display text-3xl font-medium tracking-tight text-white">
            Every AI system, accounted for.
          </h1>
          <p className="mt-2 max-w-2xl text-sm text-slate-400">
            Continuously scans your environment for AI agents, LLM providers and assistants — surfacing shadow AI before it becomes a risk.
          </p>
        </div>
        <button className="flex items-center gap-2 rounded-lg border border-white/5 bg-white/[0.02] px-3 py-2 text-[12px] text-slate-200 hover:border-white/10" data-testid="rescan-btn">
          <RefreshCcw size={13} strokeWidth={1.7} /> Rescan environment
        </button>
      </motion.div>

      <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-3">
        {[
          { label: "Discovered systems", value: stats.total, tone: "text-white" },
          { label: "Awaiting registration", value: stats.unregistered, tone: "text-[#F59E0B]" },
          { label: "Critical status", value: stats.critical, tone: "text-[#EF4444]" },
        ].map((s) => (
          <div key={s.label} className="card-eco p-5">
            <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-400">{s.label}</div>
            <div className={`mt-2 font-mono text-3xl font-light tracking-tight ${s.tone}`}>{s.value}</div>
          </div>
        ))}
      </div>

      <div className="card-eco mt-6 overflow-hidden">
        <div className="flex flex-wrap items-center gap-3 border-b border-white/5 p-4">
          <div className="flex flex-1 items-center gap-2 rounded-lg border border-white/5 bg-white/[0.02] px-3 py-1.5">
            <Search size={13} className="text-slate-500" strokeWidth={1.7} />
            <input
              data-testid="discovery-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by name, type or endpoint…"
              className="w-full bg-transparent text-[12px] text-slate-200 placeholder:text-slate-500 focus:outline-none"
            />
          </div>
          {["All", "LLM Provider", "Business Agent", "AI Assistant"].map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              data-testid={`discovery-filter-${f.toLowerCase().replace(/\s+/g, "-")}`}
              className={`rounded-lg border px-3 py-1.5 text-[12px] ${
                filter === f
                  ? "border-[#5B8DEF]/50 bg-[#5B8DEF]/10 text-[#cfe0ff]"
                  : "border-white/5 bg-white/[0.02] text-slate-400 hover:text-slate-200"
              }`}
            >
              {f}
            </button>
          ))}
        </div>

        <table className="w-full text-left text-sm">
          <thead>
            <tr className="text-[10px] uppercase tracking-[0.16em] text-slate-500">
              <th className="px-5 py-3 font-medium">Name</th>
              <th className="px-5 py-3 font-medium">Type</th>
              <th className="px-5 py-3 font-medium">Endpoint</th>
              <th className="px-5 py-3 font-medium">Status</th>
              <th className="px-5 py-3 font-medium">Risk</th>
              <th className="px-5 py-3 font-medium">Traffic</th>
              <th className="px-5 py-3 text-right font-medium">Action</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((it) => {
              const Icon = TYPE_ICON[it.type] || Bot;
              return (
                <tr key={it.id} className="border-t border-white/5 hover:bg-white/[0.02]" data-testid={`discovery-row-${it.id}`}>
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-3">
                      <div className="flex h-8 w-8 items-center justify-center rounded-lg border border-white/5 bg-white/[0.02] text-slate-300">
                        <Icon size={15} strokeWidth={1.6} />
                      </div>
                      <div>
                        <div className="text-slate-100">{it.name}</div>
                        <div className="font-mono text-[11px] text-slate-500">{it.id}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-5 py-3 text-slate-300">{it.type}</td>
                  <td className="px-5 py-3 font-mono text-[12px] text-slate-400">{it.endpoint}</td>
                  <td className="px-5 py-3">
                    <StatusPill tone={STATUS_TONE[it.status]}>{it.status}</StatusPill>
                  </td>
                  <td className="px-5 py-3">
                    <StatusPill tone={RISK_TONE[it.risk]} dot={false}>
                      {it.risk}
                    </StatusPill>
                  </td>
                  <td className="px-5 py-3 font-mono text-[12px] text-slate-300">{it.traffic}</td>
                  <td className="px-5 py-3 text-right">
                    {it.registered ? (
                      <span className="text-[12px] text-emerald-300">✓ Registered</span>
                    ) : (
                      <button
                        onClick={() => register(it.id)}
                        data-testid={`register-${it.id}`}
                        className="inline-flex items-center gap-1.5 rounded-lg bg-[#5B8DEF] px-2.5 py-1.5 text-[11px] font-medium text-[#0b1020] hover:bg-[#7AA4F5]"
                      >
                        <Plus size={12} strokeWidth={2} /> Register
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
